<template>
  <section class="payment-info-form py-6 px-4">
    <b-container fluid class="max-w-1200">
      <b-card class="border-0 shadow-sm">
        <template #header>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div>
              <h2 class="h4 text-primary mb-1">
                <i class="flaticon2-safe mr-2"></i>
                Payment Information
              </h2>
              <p class="mb-0 text-muted">
                Complete your banking information to receive payments
              </p>
            </div>
          </div>
        </template>

        <b-alert
          variant="danger"
          :show="Boolean(errorMessage)"
          dismissible
          @dismissed="errorMessage = null"
          class="mt-3"
        >
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ errorMessage }}
        </b-alert>

        <b-alert
          variant="success"
          :show="Boolean(successMessage)"
          dismissible
          @dismissed="successMessage = null"
          class="mt-3"
        >
          <i class="fas fa-check-circle mr-2"></i>
          {{ successMessage }}
        </b-alert>

        <b-form @submit.prevent="handleSubmit" v-if="!loading">
          <!-- Account Holder Information -->
          <b-card class="border-0 bg-light mb-4">
            <div class="d-flex align-items-center mb-3">
              <i class="flaticon2-user text-primary mr-3" style="font-size: 1.5rem;"></i>
              <h5 class="mb-0 text-primary font-weight-bold">Account Holder Information</h5>
            </div>
            <b-row>
              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  First Name <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.first_name"
                  placeholder="Enter your first name"
                  required
                  :state="validation.first_name"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.first_name === false">
                  First name is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Last Name <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.last_name"
                  placeholder="Enter your last name"
                  required
                  :state="validation.last_name"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.last_name === false">
                  Last name is required
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-card>

          <!-- Account Information -->
          <b-card class="border-0 bg-light mb-4">
            <div class="d-flex align-items-center mb-3">
              <i class="flaticon2-safe text-primary mr-3" style="font-size: 1.5rem;"></i>
              <h5 class="mb-0 text-primary font-weight-bold">Account Information</h5>
            </div>
            <b-row>
              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Routing Number <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.routing_number"
                  placeholder="Enter routing number"
                  required
                  :state="validation.routing_number"
                  class="form-control-lg"
                />
                <small class="form-text text-muted">Bank routing or transit number</small>
                <b-form-invalid-feedback v-if="validation.routing_number === false">
                  Routing number is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Account Number <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.account_number"
                  placeholder="Enter account number"
                  required
                  :state="validation.account_number"
                  class="form-control-lg"
                />
                <small class="form-text text-muted">Your bank account number</small>
                <b-form-invalid-feedback v-if="validation.account_number === false">
                  Account number is required
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-card>

          <!-- Account Address -->
          <b-card class="border-0 bg-light mb-4">
            <div class="d-flex align-items-center mb-3">
              <i class="flaticon2-pin text-primary mr-3" style="font-size: 1.5rem;"></i>
              <h5 class="mb-0 text-primary font-weight-bold">Account Address</h5>
            </div>
            <b-row>
              <b-col cols="12" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Street Address <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.street_address"
                  placeholder="Enter street address"
                  required
                  :state="validation.street_address"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.street_address === false">
                  Street address is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Street Address Line 2
                </label>
                <b-form-input
                  v-model="form.street_address_line_2"
                  placeholder="Apartment, suite, unit, building, floor, etc. (optional)"
                  class="form-control-lg"
                />
              </b-col>

              <b-col cols="12" md="4" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  City <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.city"
                  placeholder="Enter city"
                  required
                  :state="validation.city"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.city === false">
                  City is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="4" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  State / Province <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.state_province"
                  placeholder="Enter state or province"
                  required
                  :state="validation.state_province"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.state_province === false">
                  State or province is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="4" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Postal / Zip Code <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.postal_zip_code"
                  placeholder="Enter postal code"
                  required
                  :state="validation.postal_zip_code"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.postal_zip_code === false">
                  Postal code is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Country <span class="text-danger">*</span>
                </label>
                <b-form-select
                  v-model="form.country"
                  :options="countryOptions"
                  required
                  :state="validation.country"
                  class="form-control-lg"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      Select a country
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <b-form-invalid-feedback v-if="validation.country === false">
                  Country is required
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-card>

          <!-- Contact Information -->
          <b-card class="border-0 bg-light mb-4">
            <div class="d-flex align-items-center mb-3">
              <i class="flaticon2-phone text-primary mr-3" style="font-size: 1.5rem;"></i>
              <h5 class="mb-0 text-primary font-weight-bold">Contact Information</h5>
            </div>
            <b-row>
              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Account Phone Number <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.account_phone_number"
                  placeholder="Enter phone number"
                  required
                  :state="validation.account_phone_number"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.account_phone_number === false">
                  Phone number is required
                </b-form-invalid-feedback>
              </b-col>

              <b-col cols="12" md="6" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Account Email <span class="text-danger">*</span>
                </label>
                <b-form-input
                  v-model="form.account_email"
                  type="email"
                  placeholder="example@example.com"
                  required
                  :state="validation.account_email"
                  class="form-control-lg"
                />
                <b-form-invalid-feedback v-if="validation.account_email === false">
                  A valid email address is required
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-card>

          <!-- Additional Information -->
          <b-card class="border-0 bg-light mb-4">
            <div class="d-flex align-items-center mb-3">
              <i class="flaticon2-notepad text-primary mr-3" style="font-size: 1.5rem;"></i>
              <h5 class="mb-0 text-primary font-weight-bold">Additional Information</h5>
            </div>
            <b-row>
              <b-col cols="12" class="mb-3">
                <label class="font-weight-semibold text-dark">
                  Suggestions or topics you would like to be included?
                </label>
                <b-form-textarea
                  v-model="form.suggestions"
                  placeholder="Enter your suggestions (optional)"
                  rows="4"
                  class="form-control-lg"
                />
                <small class="form-text text-muted">Any additional information or preferences regarding payments</small>
              </b-col>
            </b-row>
          </b-card>

          <!-- Submit Button -->
          <div class="d-flex justify-content-end mt-4 pt-3 border-top">
            <b-button
              type="submit"
              variant="primary"
              :disabled="saving"
              size="lg"
              class="px-5"
            >
              <i class="fas fa-save mr-2"></i>
              <span v-if="!saving">{{ formId ? 'Update' : 'Save' }} Payment Information</span>
              <span v-else>
                <b-spinner small class="mr-2"></b-spinner>
                {{ formId ? 'Updating' : 'Saving' }}...
              </span>
            </b-button>
          </div>
        </b-form>

        <div v-else class="d-flex justify-content-center align-items-center py-5">
          <SpinnerLoading />
        </div>
      </b-card>
    </b-container>
    <ToastNotification ref="toast" />
  </section>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BContainer,
  BForm,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BFormInvalidFeedback,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import paymentToAgentsService from "@/core/services/apiInsurance/paymentToAgents";
import SpinnerLoading from "@/components/SpinnerLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";

export default {
  name: "PaymentInfoForm",
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BContainer,
    BForm,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BFormInvalidFeedback,
    BRow,
    BSpinner,
    SpinnerLoading,
    ToastNotification,
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: null,
      successMessage: null,
      formId: null,
      form: {
        first_name: "",
        last_name: "",
        routing_number: "",
        account_number: "",
        street_address: "",
        street_address_line_2: "",
        city: "",
        state_province: "",
        postal_zip_code: "",
        country: null,
        account_phone_number: "",
        account_email: "",
        suggestions: "",
      },
      validation: {},
      countryOptions: [
        { value: "afghanistan", text: "Afghanistan" },
        { value: "albania", text: "Albania" },
        { value: "algeria", text: "Algeria" },
        { value: "argentina", text: "Argentina" },
        { value: "australia", text: "Australia" },
        { value: "austria", text: "Austria" },
        { value: "bahamas", text: "Bahamas" },
        { value: "bahrain", text: "Bahrain" },
        { value: "bangladesh", text: "Bangladesh" },
        { value: "barbados", text: "Barbados" },
        { value: "belgium", text: "Belgium" },
        { value: "belize", text: "Belize" },
        { value: "bolivia", text: "Bolivia" },
        { value: "brazil", text: "Brazil" },
        { value: "bulgaria", text: "Bulgaria" },
        { value: "canada", text: "Canada" },
        { value: "chile", text: "Chile" },
        { value: "china", text: "China" },
        { value: "colombia", text: "Colombia" },
        { value: "costa_rica", text: "Costa Rica" },
        { value: "croatia", text: "Croatia" },
        { value: "cuba", text: "Cuba" },
        { value: "czech_republic", text: "Czech Republic" },
        { value: "denmark", text: "Denmark" },
        { value: "dominican_republic", text: "Dominican Republic" },
        { value: "ecuador", text: "Ecuador" },
        { value: "egypt", text: "Egypt" },
        { value: "el_salvador", text: "El Salvador" },
        { value: "spain", text: "Spain" },
        { value: "united_states", text: "United States" },
        { value: "philippines", text: "Philippines" },
        { value: "finland", text: "Finland" },
        { value: "france", text: "France" },
        { value: "germany", text: "Germany" },
        { value: "greece", text: "Greece" },
        { value: "guatemala", text: "Guatemala" },
        { value: "haiti", text: "Haiti" },
        { value: "honduras", text: "Honduras" },
        { value: "hong_kong", text: "Hong Kong" },
        { value: "hungary", text: "Hungary" },
        { value: "india", text: "India" },
        { value: "indonesia", text: "Indonesia" },
        { value: "ireland", text: "Ireland" },
        { value: "israel", text: "Israel" },
        { value: "italy", text: "Italy" },
        { value: "jamaica", text: "Jamaica" },
        { value: "japan", text: "Japan" },
        { value: "jordan", text: "Jordan" },
        { value: "kenya", text: "Kenya" },
        { value: "south_korea", text: "South Korea" },
        { value: "kuwait", text: "Kuwait" },
        { value: "mexico", text: "Mexico" },
        { value: "nicaragua", text: "Nicaragua" },
        { value: "norway", text: "Norway" },
        { value: "panama", text: "Panama" },
        { value: "paraguay", text: "Paraguay" },
        { value: "peru", text: "Peru" },
        { value: "poland", text: "Poland" },
        { value: "portugal", text: "Portugal" },
        { value: "puerto_rico", text: "Puerto Rico" },
        { value: "united_kingdom", text: "United Kingdom" },
        { value: "romania", text: "Romania" },
        { value: "russia", text: "Russia" },
        { value: "singapore", text: "Singapore" },
        { value: "south_africa", text: "South Africa" },
        { value: "sweden", text: "Sweden" },
        { value: "switzerland", text: "Switzerland" },
        { value: "thailand", text: "Thailand" },
        { value: "turkey", text: "Turkey" },
        { value: "ukraine", text: "Ukraine" },
        { value: "uruguay", text: "Uruguay" },
        { value: "venezuela", text: "Venezuela" },
        { value: "vietnam", text: "Vietnam" },
        { value: "other", text: "Other" },
      ],
    };
  },
  methods: {
    getLocalStorageUserData() {
      if (typeof window === "undefined") return null;
      const raw = window.localStorage.getItem("userData");
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (error) {
        return null;
      }
    },
    getAgentId() {
      const userData = this.getLocalStorageUserData();
      return userData?.id || null;
    },
    validateForm() {
      this.validation = {};
      let isValid = true;

      const requiredFields = [
        "first_name",
        "last_name",
        "routing_number",
        "account_number",
        "street_address",
        "city",
        "state_province",
        "postal_zip_code",
        "country",
        "account_phone_number",
        "account_email",
      ];

      requiredFields.forEach((field) => {
        if (!this.form[field] || this.form[field].toString().trim() === "") {
          this.validation[field] = false;
          isValid = false;
        } else {
          this.validation[field] = true;
        }
      });

      // Validate email
      if (this.form.account_email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.account_email)) {
          this.validation.account_email = false;
          isValid = false;
        }
      }

      return isValid;
    },
    async loadPaymentInfo() {
      const agentId = this.getAgentId();
      if (!agentId) {
        this.errorMessage = "Unable to retrieve agent ID";
        return;
      }

      this.loading = true;
      this.errorMessage = null;

      try {
        const response = await paymentToAgentsService.getPaymentInfo(agentId);
        if (response?.status === "OK" && response?.data) {
          const data = response.data;
          this.formId = data.id;
          this.form = {
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            routing_number: data.routing_number || "",
            account_number: data.account_number || "",
            street_address: data.street_address || "",
            street_address_line_2: data.street_address_line_2 || "",
            city: data.city || "",
            state_province: data.state_province || "",
            postal_zip_code: data.postal_zip_code || "",
            country: data.country || null,
            account_phone_number: data.account_phone_number || "",
            account_email: data.account_email || "",
            suggestions: data.suggestions || "",
          };
        }
      } catch (error) {
        // If error is 404, it means there's no saved information yet
        if (error?.response?.status !== 404) {
          this.errorMessage =
            error?.response?.data?.message ||
            "Error loading payment information";
        }
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        const errorMsg = "Please complete all required fields";
        this.errorMessage = errorMsg;
        if (this.$refs.toast) {
          this.$refs.toast.warning(errorMsg);
        }
        return;
      }

      this.saving = true;
      this.errorMessage = null;
      this.successMessage = null;

      try {
        const agentId = this.getAgentId();
        if (!agentId) {
          throw new Error("Unable to retrieve agent ID");
        }

        if (this.formId) {
          // Update existing information
          const updatePayload = { ...this.form };
          delete updatePayload.agent_id;
          await paymentToAgentsService.updatePaymentInfo(this.formId, updatePayload);
          this.successMessage = "Payment information updated successfully";
          if (this.$refs.toast) {
            this.$refs.toast.success("Payment information updated successfully");
          }
        } else {
          // Create new information
          const createPayload = {
            agent_id: agentId,
            ...this.form,
          };
          const response = await paymentToAgentsService.createPaymentInfo(createPayload);
          if (response?.registro?.id) {
            this.formId = response.registro.id;
          }
          this.successMessage = "Payment information saved successfully";
          if (this.$refs.toast) {
            this.$refs.toast.success("Payment information saved successfully");
          }
        }

        // Emit event to notify that information was saved
        this.$emit("payment-info-saved");
      } catch (error) {
        const errorMsg =
          error?.response?.data?.message ||
          "Error saving payment information";
        this.errorMessage = errorMsg;
        if (this.$refs.toast) {
          this.$refs.toast.danger(errorMsg);
        }
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadPaymentInfo();
  },
};
</script>

<style scoped>
.payment-info-form {
  background-color: #f4f6f8;
  min-height: 100vh;
}

.max-w-1200 {
  max-width: 1200px;
  margin: 0 auto;
}

.payment-info-form h2 {
  font-weight: 700;
}

.payment-info-form label {
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.payment-info-form .text-primary {
  color: #1e88e5 !important;
}


.bg-light {
  background-color: #f8f9fa !important;
}

.form-control-lg {
  border-radius: 0.5rem;
  border: 1px solid #e4e9f2;
  transition: all 0.3s ease;
}

.form-control-lg:focus {
  border-color: #1e88e5;
  box-shadow: 0 0 0 0.2rem rgba(30, 136, 229, 0.25);
}

.border-top {
  border-top: 2px solid #e4e9f2 !important;
}
</style>
